import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './PatientFlows.styled';
import PatientAnalysesList from 'components/Analyses/PatientAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import usePatientFlows from 'hooks/usePatientFlows';
import usePatientAnalyses from 'hooks/usePatientAnalyses';
import { DELETE_PATIENT_FLOW } from 'helpers/confirmations';
import { patientFlowsSelectors } from "features/patientFlows";

const PatientFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getPatientFlow, createPatientFlow, updatePatientFlow } = usePatientFlows();
  const { getPatientAnalyses, clearLocalPatientAnalyses } = usePatientAnalyses();
  const id = useCustomSelector(patientFlowsSelectors.getPatientFlowId);
  const fetching = useCustomSelector(patientFlowsSelectors.patientFlowIsFetching);
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (patient_flow) => {
    setPrompt({ value: patient_flow?.prompt ?? '', error: '' });
  };
  const handleExited = () => {
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    const patient_flow = {
      prompt: prompt.value
    };

    if (!_isEmpty(patient_flow)) {
      if (!!id) {
        updatePatientFlow(patient_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createPatientFlow(patient_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_PATIENT_FLOW });
  const fetchPatientAnalyses = useCallback(() => {
    getPatientAnalyses({ offset: 0 })
  }, [getPatientAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalPatientAnalyses();
    };
  }, [id, clearLocalPatientAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getPatientFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { patient_flow } = payload;
          handleEntering(patient_flow);
          fetchPatientAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getPatientFlow, fetchPatientAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <PatientAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <PatientFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);